import axios from "axios";

/**
 * Read the finance service's own error message off a failed request.
 *
 * ── Why the shared `extractValidationErrors` is not enough here ───────────────────────────────
 *
 * That helper is built for Laravel's `{ message, errors: { field: [] } }` 422. The finance
 * service is NestJS and answers two different shapes:
 *
 *   deliberate throw   { success: false, statusCode: 409, message: "…", timestamp }
 *   ValidationPipe     { success: false, statusCode: 400, message: ["a must be…", "b must…"] }
 *
 * — `message` is a STRING in the first and an ARRAY in the second, and `extractValidationErrors`
 * would wrap the array in another array and render `[object Object]`-ish output.
 *
 * ── Why the message is surfaced verbatim ─────────────────────────────────────────────────────
 *
 * The invoicing flows have four deliberate, operator-actionable 4xx messages that a generic
 * "Failed to record payment" would destroy:
 *
 *   400 "Payment exceeds the balance due on this invoice"
 *   400 "Stripe-collected invoices can only be settled in full out of band. Record the full
 *        balance, or void and re-issue as a local invoice."
 *   400 "Re-supply line items to change price entry mode on this invoice"
 *   409 the invoice is paid / cancelled / imported and cannot be mutated
 *
 * This is safe *for this service specifically*: finance's `httpErrorFrom` only ever surfaces a
 * message the service wrote itself and collapses anything arbitrary (a `QueryFailedError`'s SQL,
 * a Stripe error's email) to a fixed fallback before it leaves the process. Do not copy this
 * verbatim-surfacing pattern to a backend that has no such rule.
 */
export function financeErrorMessage(error: unknown, fallback: string): string {
	if (axios.isAxiosError(error) && error.response) {
		const data = error.response.data as { message?: string | string[] } | undefined;
		const message = data?.message;

		if (Array.isArray(message)) {
			const joined = message.filter((m) => typeof m === "string" && m.length > 0).join(". ");
			if (joined) {
				return joined;
			}
		} else if (typeof message === "string" && message.length > 0) {
			return message;
		}

		return fallback;
	}

	if (error instanceof Error && error.message) {
		return error.message;
	}

	return fallback;
}

/** The HTTP status of a failed request, or `null` when the failure never reached the server. */
export function financeErrorStatus(error: unknown): number | null {
	return axios.isAxiosError(error) && error.response ? error.response.status : null;
}

/**
 * True when the failure is the mutation guard refusing a paid / cancelled / imported invoice.
 * Worth distinguishing because the fix is never "retry" — the row is final and the list is stale.
 */
export function isConflict(error: unknown): boolean {
	return financeErrorStatus(error) === 409;
}
